import {
  searchReportingRouteSchemas,
  type SearchReportingRoute,
} from '@charitystack/org-engagement-contracts';
import { safeFetchJSON } from '@charitystack/shared';

import {
  createSearchReportingClient,
  isShortSearchReportingRoute,
} from '../trpc/search-reporting-22-routes';

/** Code-only registry. Population and traffic activation require separate rollout approval. */
export const searchReportingShortReadsRollout = Object.freeze({
  key: 'org_engagement_admin_api_cutover_org_engagement',
  organizations: Object.freeze([] as readonly { organizationId: string; enabled: boolean }[]),
});

export const searchReportingLongReadsRollout = Object.freeze({
  key: 'org_engagement_admin_api_cutover_reporting',
  organizations: Object.freeze([] as readonly { organizationId: string; enabled: boolean }[]),
});

/** The only dashboard read of the legacy transport URL. */
export const searchReportingLegacyUrl = import.meta.env.VITE_APP_OPENSEARCH_URL ?? '';

const routeByRequest = new Map<string, SearchReportingRoute>(
  Object.keys(searchReportingRouteSchemas).map((route) => [route, route as SearchReportingRoute]),
);
const listQueryKeys = new Set(['subscriptionIDs', 'formIDs']);

function authorizationHeader(init: RequestInit | undefined): string {
  return new Headers(init?.headers).get('authorization') ?? '';
}

function organizationIdFromAuthorization(authorization: string): string | undefined {
  try {
    const payload = authorization.replace(/^Bearer\s+/i, '').split('.')[1];
    if (!payload) return undefined;
    const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
    const claims = JSON.parse(
      atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')),
    ) as Record<string, unknown>;
    for (const key of ['org_id', 'orgId', 'organization_id'])
      if (typeof claims[key] === 'string' && claims[key]) return claims[key];
  } catch {
    // A malformed or non-Clerk token must remain on the legacy path.
  }
  return undefined;
}

function routeInput(method: string, url: URL, init: RequestInit | undefined): unknown {
  if (method === 'POST') {
    if (typeof init?.body !== 'string') throw new Error('SEARCH_REPORTING_BODY_NOT_JSON');
    return JSON.parse(init.body);
  }
  return Object.fromEntries(
    [...url.searchParams.entries()].map(([key, value]) => [
      key,
      listQueryKeys.has(key) ? value.split(',').filter(Boolean) : value,
    ]),
  );
}

type RoutingOverrides = {
  shortReadRegistry?: unknown;
  longReadRegistry?: unknown;
  longReadUrl?: unknown;
  shortReadUrl?: unknown;
  legacyUrl?: string;
};

export function resolveSearchReportingUrl(
  input: RequestInfo | URL,
  init?: RequestInit,
  overrides: RoutingOverrides = {},
): RequestInfo | URL {
  try {
    const legacyUrl = overrides.legacyUrl ?? searchReportingLegacyUrl;
    if (!legacyUrl) return input;
    const legacyBase = new URL(legacyUrl);
    const requested = new URL(String(input), legacyBase);
    const normalizedBasePath = legacyBase.pathname.replace(/\/$/, '');
    if (
      requested.origin !== legacyBase.origin ||
      !requested.pathname.startsWith(`${normalizedBasePath}/`)
    )
      return input;
    const path = requested.pathname.slice(normalizedBasePath.length);
    const method = (init?.method ?? 'GET').toUpperCase();
    const route = routeByRequest.get(`${method} ${path}`);
    if (!route) return input;
    const authorization = authorizationHeader(init);
    const registry = isShortSearchReportingRoute(route)
      ? (overrides.shortReadRegistry ?? searchReportingShortReadsRollout.organizations)
      : (overrides.longReadRegistry ?? searchReportingLongReadsRollout.organizations);
    const client = selectSearchReportingClient({
      organizationId: organizationIdFromAuthorization(authorization),
      authorization,
      registry,
      longReadUrl: overrides.longReadUrl ?? import.meta.env.VITE_APP_REPORTING_URL,
      shortReadUrl: overrides.shortReadUrl ?? import.meta.env.VITE_APP_ORG_ENGAGEMENT_URL,
    });
    if (!client) return input;
    return client.urlFor(route, routeInput(method, requested, init) as never);
  } catch {
    return input;
  }
}

export function searchReportingFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  overrides?: RoutingOverrides & { fetch?: typeof fetch },
) {
  return (overrides?.fetch ?? fetch)(resolveSearchReportingUrl(input, init, overrides), init);
}

export function searchReportingFetchJSON(
  input: RequestInfo | URL,
  init?: RequestInit,
  config?: Record<string, unknown>,
) {
  return safeFetchJSON(String(resolveSearchReportingUrl(input, init)), init, config);
}

export function selectSearchReportingClient({
  organizationId,
  authorization,
  registry,
  longReadUrl = import.meta.env.VITE_APP_REPORTING_URL,
  shortReadUrl = import.meta.env.VITE_APP_ORG_ENGAGEMENT_URL,
}: {
  organizationId: unknown;
  authorization: string;
  registry?: unknown;
  longReadUrl?: unknown;
  shortReadUrl?: unknown;
}) {
  try {
    if (typeof organizationId !== 'string' || !organizationId || !Array.isArray(registry))
      return null;
    const seen = new Set<string>();
    let enabled = false;
    for (const row of registry) {
      if (
        !row ||
        typeof row !== 'object' ||
        Object.keys(row).sort().join(',') !== 'enabled,organizationId' ||
        typeof row.organizationId !== 'string' ||
        !row.organizationId ||
        typeof row.enabled !== 'boolean' ||
        seen.has(row.organizationId)
      )
        return null;
      seen.add(row.organizationId);
      if (row.organizationId === organizationId) enabled = row.enabled;
    }
    if (
      !enabled ||
      !authorization ||
      typeof longReadUrl !== 'string' ||
      typeof shortReadUrl !== 'string'
    )
      return null;
    for (const rawUrl of [longReadUrl, shortReadUrl]) {
      const url = new URL(rawUrl);
      if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash)
        return null;
    }
    return createSearchReportingClient({ longReadUrl, shortReadUrl, authorization });
  } catch {
    return null;
  }
}
